/**
 * Studio text for what staff hear and see in the inbox, in English, the source language: sound and
 * desktop alerts, unread chats, online/away/offline and typing. Kept apart from the main file so it
 * can grow on its own; merged under `inboxAlerts`.
 */
export const inboxAlertsEn = {
  status: {
    label: 'Your status',
    online: 'Online',
    away: 'Away',
    offline: 'Offline',
    hint: 'Customers are told a person is online only while someone who can answer is online.',
    idle: 'Your team sees you as away: Studio has not been used for a few minutes.',
    changed: 'Your status is now {{status}}.',
    teammate: '{{name}}, {{status}}',
  },
  settings: {
    button: 'Alerts',
    title: 'Sounds and desktop alerts',
    description: 'These settings are only yours. Each teammate keeps their own.',
    mute: 'Mute all sounds',
    muteHint: 'Desktop alerts and the count on the browser tab still work.',
    volume: 'Volume',
    volumeValue: '{{value}} percent',
    silent: 'No sound will play while sounds are muted or the volume is at 0.',
    events: {
      title: 'Sound for each event',
      newChat: 'A chat is waiting for a person, or is past its reply time',
      customerMessage: 'A customer writes in your chat, or in a chat nobody has taken',
      assigned: 'A chat is offered, assigned or transferred to you',
    },
    sounds: {
      chime: 'Chime',
      pop: 'Pop',
      bell: 'Bell',
      drop: 'Drop',
      ping: 'Ping',
      none: 'No sound',
    },
    play: 'Play {{sound}}',
    blocked: 'Your browser kept the sound silent. Click anywhere on the page once, then try again.',
    saveFailed: 'Your last change could not be saved. The settings show what is saved.',
    desktop: {
      title: 'Desktop alerts',
      enabled: 'Show a desktop alert when Studio is in the background',
      enabledHint: 'You get one for each new chat, message or assignment while you are elsewhere.',
      preview: 'Show the start of the customer’s message',
      previewHint: 'Turn this off if other people can see your screen.',
      unsupported: 'This browser cannot show desktop alerts.',
      prompt: 'Your browser will ask for permission. Choose Allow.',
      allow: 'Allow desktop alerts',
      granted: 'Desktop alerts are allowed in this browser.',
      denied:
        'Desktop alerts are blocked in this browser. To turn them on, allow notifications for this site in your browser settings, then reload this page.',
      test: 'Send a test alert',
      testTitle: 'Hamnivo test alert',
      testBody: 'This is how a desktop alert looks. Click it to come back to Studio.',
    },
  },
  banner: {
    title: 'Hear about new chats while Studio is in the background',
    description:
      'Allow desktop alerts to be told about new chats and messages while you work in another tab or window.',
    allow: 'Allow desktop alerts',
    later: 'Not now',
  },
  alert: {
    chat_waiting: '{{name}} is waiting for a person',
    sla_overdue: '{{name}} is past the reply-time target',
    sla_overdue_lead: 'Nobody has replied to {{name}} in time',
    chat_offered: '{{name}} is offered to you: accept or decline',
    customer_message: 'New message from {{name}}',
    assigned: 'A chat was assigned to you',
    assignedBody: '{{name}}: {{preview}}',
    open: 'Open the chat to read it.',
  },
  unread: {
    row: 'Unread messages: {{count}}',
  },
  typing: {
    customer: '{{name}} is typing…',
  },
  visitor: {
    title: 'Website visit',
    notNoted: 'This brand does not note which pages visitors view.',
    none: 'No pages are noted for this customer. They may use Do Not Track, or the chat was opened where the website’s chat script does not run.',
    online: 'On the website now',
    left: 'Left the website {{when}}',
    currentPage: 'Current page',
    entryPage: 'Entered on',
    visits: 'Visits',
    visitor: 'Visitor',
    returning: 'Returning',
    new: 'New',
    timeOnSite: 'Time on site',
    underMinute: 'Less than a minute',
    country: 'Country',
    device: 'Device',
    language: 'Language',
    cameFrom: 'Came from',
    pages: 'Pages in this visit',
    untitled: 'Untitled page',
    openPage: 'Open {{page}} in a new tab',
    types: { desktop: 'Computer', mobile: 'Phone', tablet: 'Tablet', other: 'Device' },
    privacy: 'Pages are noted without query strings and kept for 30 days. No IP address is stored.',
  },
  live: {
    link: 'Live visitors',
    title: 'Live visitors',
    description: 'People on your websites right now. The list updates every few seconds.',
    back: 'Back to inbox',
    brand: 'Brand',
    allBrands: 'All brands',
    loading: 'Loading visitors',
    summary: 'On the website now: {{number}}',
    updated: 'Updated {{time}}',
    table: 'Visitors on the website now',
    columns: {
      visitor: 'Visitor',
      page: 'Page',
      time: 'Time on site',
      country: 'Country',
      device: 'Device',
      visits: 'Visits',
      actions: 'Actions',
    },
    returning: 'Returning · {{number}}',
    new: 'New',
    openChat: 'Open chat',
    openChatFor: 'Open the chat with {{name}}',
    invite: 'Start chat',
    inviteFor: 'Start a chat with {{name}}',
    empty: {
      title: 'Nobody is on your website right now',
      description:
        'Visitors appear here while they have a page open. This needs the chat script on your website and page tracking turned on for the brand.',
    },
    forbidden: {
      title: 'Live visitors are not available to you',
      description: 'Your role does not include reading conversations.',
    },
    dialog: {
      title: 'Start a chat with {{name}}',
      description:
        'Your message appears next to the chat button on the page they are viewing. If they open it, the chat starts with your words.',
      message: 'Your message',
      hint: 'Up to 300 characters.',
      default: 'Hi! Can I help you find something?',
      send: 'Send invitation',
      sent: 'Invitation sent. It shows within a few seconds and waits for 3 minutes.',
      gone: 'This visitor has left the website.',
    },
    settings: {
      title: 'Visitor tracking',
      description: 'Choose whether a brand notes which pages visitors view.',
      brand: 'Brand',
      tracking: 'Note which pages visitors view',
      trackingHint:
        'On by default. Visitors whose browser sends Do Not Track or Global Privacy Control are always left out, and the chat tells visitors that pages are noted.',
      queries: 'Keep the part of a page address after the question mark',
      queriesHint:
        'Off by default: it is removed before anything is stored, because it can hold email addresses or order numbers. When it is on, values that look like passwords or tokens are still removed.',
      retention:
        'Pages and visits are deleted 30 days after the visitor was last seen. No IP address is stored.',
      save: 'Save',
      saved: 'Saved. It applies from each visitor’s next page view.',
    },
  },
  library: {
    link: 'Saved replies and tags',
    title: 'Saved replies and tags',
    description:
      'Ready-made answers and labels your team shares. A change applies to everyone who answers customers for this brand.',
    back: 'Back to inbox',
    brand: 'Brand',
    loading: 'Loading saved replies and tags',
    noBrands: 'There is no brand yet. Saved replies and tags belong to a brand’s chat.',
    notSetUp: {
      title: 'Chat is not set up for {{name}} yet',
      description:
        'Connect the brand to chat first. Its saved replies and tags can be set up here afterwards.',
      action: 'Open Brands and install',
    },
    forbidden: {
      title: 'Saved replies and tags are not available to you',
      description: 'Your role does not include managing the inbox. Ask an administrator for help.',
    },
    hint: {
      add: 'Add tags',
      ask: 'Ask someone who manages the inbox to add them.',
    },
    replies: {
      title: 'Saved replies',
      description:
        'Your team picks these with the Saved replies button next to the reply box. Details such as the customer’s name are filled in from the chat.',
      add: 'Add a saved reply',
      search: 'Search saved replies',
      noMatch: 'No saved reply matches your search.',
      table: 'Saved replies of this brand',
      columns: { code: 'Short code', text: 'Text', actions: 'Actions' },
      edit: 'Edit',
      editFor: 'Edit saved reply {{code}}',
      delete: 'Delete',
      deleteFor: 'Delete saved reply {{code}}',
      empty: {
        title: 'No saved replies yet',
        description:
          'Add the answers your team writes again and again, such as opening hours or how returns work.',
      },
      added: 'Saved reply {{code}} added.',
      saved: 'Saved reply {{code}} saved.',
      deleted: 'Saved reply {{code}} deleted.',
      dialog: {
        addTitle: 'Add a saved reply',
        editTitle: 'Edit saved reply {{code}}',
        description: 'Everyone who answers customers for this brand can use it straight away.',
        code: 'Short code',
        codeHint:
          'A short name your team can search for, such as refund-policy. Letters, numbers, - and _ only, no spaces.',
        codeRequired: 'Enter a short code.',
        codeInvalid: 'Use letters, numbers, - and _ only (no spaces), up to 40 characters.',
        codeTaken: 'Another saved reply already uses this short code.',
        text: 'Text',
        textHint: 'Up to {{number}} characters.',
        textRequired: 'Enter the text of the reply.',
        details: 'Add a detail from the chat',
        detailsHint:
          'It is filled in when someone uses the reply. If it is not known, the person is asked to fill it in by hand.',
        detail: {
          firstName: 'Customer’s first name',
          name: 'Customer’s full name',
          agent: 'Your name',
          brand: 'Brand name',
        },
        save: 'Save',
        add: 'Add saved reply',
      },
      confirm: {
        title: 'Delete saved reply {{code}}?',
        body: 'It disappears for everyone on your team. Messages that were already sent do not change.',
        action: 'Delete saved reply',
      },
    },
    tags: {
      title: 'Tags',
      description:
        'Labels your team puts on chats to sort and find them. Staff add them in a chat’s details panel.',
      add: 'Add a tag',
      table: 'Tags of this brand',
      columns: { name: 'Name', description: 'Description', actions: 'Actions' },
      edit: 'Edit',
      editFor: 'Edit tag {{name}}',
      delete: 'Delete',
      deleteFor: 'Delete tag {{name}}',
      empty: {
        title: 'No tags yet',
        description: 'Add tags such as vip or refund, so your team can sort chats.',
      },
      added: 'Tag {{name}} added.',
      saved: 'Tag {{name}} saved.',
      deleted: 'Tag {{name}} deleted.',
      dialog: {
        addTitle: 'Add a tag',
        editTitle: 'Edit tag {{name}}',
        description: 'Your team can put it on chats straight away.',
        name: 'Name',
        nameHint:
          'At least 2 letters or numbers, with - and _ allowed after the first one, such as vip or order-problem. It is kept in lower case.',
        nameRequired: 'Enter a name for the tag.',
        nameInvalid:
          'Use 2 to 50 letters or numbers, with - and _ allowed after the first one and no spaces. The chat service does not accept letters with vowel signs (such as in Hindi): write the name in English letters instead.',
        nameTaken: 'A tag with this name already exists.',
        renameNote: 'Renaming changes this tag on every chat that has it.',
        descriptionLabel: 'Description',
        descriptionHint: 'Optional. Up to {{number}} characters.',
        color: 'Colour',
        colorChosen: 'Chosen colour: {{name}}',
        save: 'Save',
        add: 'Add tag',
      },
      colors: {
        blue: 'Blue',
        green: 'Green',
        teal: 'Teal',
        orange: 'Orange',
        red: 'Red',
        pink: 'Pink',
        purple: 'Purple',
        grey: 'Grey',
        custom: 'Custom ({{hex}})',
      },
      confirm: {
        title: 'Delete tag {{name}}?',
        body: 'It is removed from the list your team picks from, and from every chat that has it.',
        action: 'Delete tag',
      },
    },
  },
} as const;

type Strings<T> = { [K in keyof T]: T[K] extends string ? string : Strings<T[K]> };
/** Every locale has exactly the English keys; TypeScript reports a missing one. */
export type InboxAlertsTranslation = Strings<typeof inboxAlertsEn>;
